import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    useTheme,
    useMediaQuery,
    Typography,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    IconButton,
    Tooltip,
} from '@mui/material';
import { useState, useMemo } from "react";
import { RestaurantSubmission, RestaurantSubmissionStatus } from '../../../../../models/DB';
import { useStoreConfig } from '../../../../../contexts/StoreConfigContext';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestaurantSubmissionRow from './RestaurantSubmissionRow';

interface Props {
    submissions: RestaurantSubmission[];
    onRefresh: () => void;
}

export const RestaurantSubmissionsTable = ({ submissions, onRefresh }: Props) => {
    const theme = useTheme();
    const { config } = useStoreConfig();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [orderBy, setOrderBy] = useState<keyof RestaurantSubmission>('created');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [statusFilter, setStatusFilter] = useState<RestaurantSubmissionStatus | 'all'>('all');
    const [searchQuery, setSearchQuery] = useState('');

    // Calculate status statistics
    const statusStats = useMemo(() => {
        const pending = submissions.filter((s) => s.status === 'pending').length;
        const approved = submissions.filter((s) => s.status === 'approved').length;
        const rejected = submissions.filter((s) => s.status === 'rejected').length;
        const contacted = submissions.filter((s) => s.status === 'contacted').length;
        return { pending, approved, rejected, contacted, total: submissions.length };
    }, [submissions]);

    // Filter and sort submissions
    const filteredSubmissions = useMemo(() => {
        let result = [...submissions];

        // Filter by status
        if (statusFilter !== 'all') {
            result = result.filter((s) => s.status === statusFilter);
        }

        // Filter by search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            result = result.filter(
                (s) =>
                    s.registered_name?.toLowerCase().includes(query) ||
                    s.trading_name?.toLowerCase().includes(query) ||
                    s.mobile?.includes(query) ||
                    s.telephone?.includes(query) ||
                    s.accounts_email?.toLowerCase().includes(query)
            );
        }

        // Sort
        result.sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];

            if (aValue === undefined || aValue === null) return 1;
            if (bValue === undefined || bValue === null) return -1;

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return order === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            return order === 'asc'
                ? aValue < bValue
                    ? -1
                    : 1
                : aValue > bValue
                ? -1
                : 1;
        });

        return result;
    }, [submissions, statusFilter, searchQuery, order, orderBy]);

    const handleRequestSort = (property: keyof RestaurantSubmission) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedSubmissions = filteredSubmissions.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Box sx={{ width: '100%' }}>
            {/* Stats Cards */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    mb: 3,
                    flexWrap: 'wrap',
                }}
            >
                <Paper
                    sx={{
                        p: 2,
                        flex: 1,
                        minWidth: 120,
                        textAlign: 'center',
                        cursor: 'pointer',
                        border: statusFilter === 'all' ? `2px solid ${config.primaryColor}` : 'none',
                    }}
                    onClick={() => setStatusFilter('all')}
                >
                    <Typography variant='h4' fontWeight={600}>
                        {statusStats.total}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        Total
                    </Typography>
                </Paper>
                <Paper
                    sx={{
                        p: 2,
                        flex: 1,
                        minWidth: 120,
                        textAlign: 'center',
                        cursor: 'pointer',
                        border: statusFilter === 'pending' ? `2px solid ${theme.palette.warning.main}` : 'none',
                    }}
                    onClick={() => setStatusFilter('pending')}
                >
                    <Typography variant='h4' fontWeight={600} color='warning.main'>
                        {statusStats.pending}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        Pending
                    </Typography>
                </Paper>
                <Paper
                    sx={{
                        p: 2,
                        flex: 1,
                        minWidth: 120,
                        textAlign: 'center',
                        cursor: 'pointer',
                        border: statusFilter === 'contacted' ? `2px solid ${theme.palette.info.main}` : 'none',
                    }}
                    onClick={() => setStatusFilter('contacted')}
                >
                    <Typography variant='h4' fontWeight={600} color='info.main'>
                        {statusStats.contacted}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        Contacted
                    </Typography>
                </Paper>
                <Paper
                    sx={{
                        p: 2,
                        flex: 1,
                        minWidth: 120,
                        textAlign: 'center',
                        cursor: 'pointer',
                        border: statusFilter === 'approved' ? `2px solid ${theme.palette.success.main}` : 'none',
                    }}
                    onClick={() => setStatusFilter('approved')}
                >
                    <Typography variant='h4' fontWeight={600} color='success.main'>
                        {statusStats.approved}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        Approved
                    </Typography>
                </Paper>
                <Paper
                    sx={{
                        p: 2,
                        flex: 1,
                        minWidth: 120,
                        textAlign: 'center',
                        cursor: 'pointer',
                        border: statusFilter === 'rejected' ? `2px solid ${theme.palette.error.main}` : 'none',
                    }}
                    onClick={() => setStatusFilter('rejected')}
                >
                    <Typography variant='h4' fontWeight={600} color='error.main'>
                        {statusStats.rejected}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        Rejected
                    </Typography>
                </Paper>
            </Box>

            {/* Controls */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    mb: 2,
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'center',
                }}
            >
                <TextField
                    placeholder='Search by name, mobile, email...'
                    size='small'
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ flex: 1, maxWidth: isMobile ? '100%' : 400 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>
                                <SearchIcon color='action' />
                            </InputAdornment>
                        ),
                    }}
                />
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <FormControl size='small' sx={{ minWidth: 150 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={statusFilter}
                            label='Status'
                            onChange={(e) => setStatusFilter(e.target.value as RestaurantSubmissionStatus | 'all')}
                        >
                            <MenuItem value='all'>All Status</MenuItem>
                            <MenuItem value='pending'>Pending</MenuItem>
                            <MenuItem value='contacted'>Contacted</MenuItem>
                            <MenuItem value='approved'>Approved</MenuItem>
                            <MenuItem value='rejected'>Rejected</MenuItem>
                        </Select>
                    </FormControl>
                    <Tooltip title='Refresh'>
                        <IconButton onClick={onRefresh}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Table */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)' }}>
                    <Table stickyHeader size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 50 }} />
                                <TableCell sortDirection={orderBy === 'created' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'created'}
                                        direction={orderBy === 'created' ? order : 'asc'}
                                        onClick={() => handleRequestSort('created')}
                                    >
                                        Date
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === 'registered_name' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'registered_name'}
                                        direction={orderBy === 'registered_name' ? order : 'asc'}
                                        onClick={() => handleRequestSort('registered_name')}
                                    >
                                        Business Name
                                    </TableSortLabel>
                                </TableCell>
                                {!isMobile && (
                                    <>
                                        <TableCell>Business Type</TableCell>
                                        <TableCell>Contact</TableCell>
                                    </>
                                )}
                                <TableCell sortDirection={orderBy === 'status' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'status'}
                                        direction={orderBy === 'status' ? order : 'asc'}
                                        onClick={() => handleRequestSort('status')}
                                    >
                                        Status
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedSubmissions.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={isMobile ? 4 : 6} align='center' sx={{ py: 8 }}>
                                        <Typography color='text.secondary'>
                                            {searchQuery || statusFilter !== 'all'
                                                ? 'No submissions match your filters'
                                                : 'No submissions yet'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedSubmissions.map((submission) => (
                                    <RestaurantSubmissionRow
                                        key={submission.id}
                                        submission={submission}
                                        onUpdate={onRefresh}
                                        isMobile={isMobile}
                                    />
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component='div'
                    count={filteredSubmissions.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </Box>
    );
};

export default RestaurantSubmissionsTable;
